/**
 * ChatConnectPrompt — surfaces the Connect Google / Connect Microsoft affordance
 * in the new-chat / empty greeting area when the workspace has NO email/calendar
 * provider connected. Product intent: let an unconnected user start the OAuth
 * grant straight from chat (in addition to the Email module's own empty state).
 *
 * Self-contained presentational leaf:
 *   - runs the shared integrations-list query (same key as the contacts/oauth
 *     consumers, so the cache is warm) gated on `enabled: Boolean(token)`;
 *   - renders null while loading OR when any active Google/Microsoft email/
 *     calendar integration already exists;
 *   - otherwise renders the two connect buttons + a one-line prompt.
 *
 * Connect goes through the ONE connect helper, `connectProvider` (same-window
 * redirect). It mirrors the email module's connect handler
 * (`useEmailModuleEvents.handleConnectEmail`): Google dispatches the
 * `triggerGoogleOAuth` window event; Microsoft does a `connectProvider` redirect
 * with `capability: 'mail'`. The post-connect refetch runs on RETURN (App.tsx
 * fires `xavior:integration-connected` → MainLayout invalidates the
 * email/integration/objects query groups).
 */
import { useQuery } from '@tanstack/react-query';
import { useSession } from '@/features/auth/hooks/useSession';
import { queryKeys } from '@/lib/query/keys';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { listIntegrations } from '@/lib/api/endpoints/integrations';
import { connectProvider } from '@/features/oauth/lib/connect-provider';
// Logos are sourced from `@/common` (the same SVGs `modules/email/provider-display`
// re-exports) rather than the email module, so this leaf adds no features→modules edge.
import googleLogo from '@/common/google-logo.svg';
import microsoftLogo from '@/common/microsoft-logo.svg';

/** Providers whose presence counts as "email/calendar connected". */
const EMAIL_CALENDAR_PROVIDERS = new Set(['google', 'microsoft']);

export function ChatConnectPrompt({ conversationId }: { conversationId?: string }) {
  const { token } = useSession();
  const workspaceId = getActiveWorkspaceId();
  const { data, isLoading } = useQuery({
    queryKey: queryKeys.integrations.list(workspaceId),
    queryFn: () => listIntegrations(token),
    enabled: Boolean(token),
    staleTime: 30_000,
  });

  // Don't flash the prompt before we know the connection state.
  if (!token || isLoading) return null;

  const hasProvider = (data ?? []).some(
    (i) => EMAIL_CALENDAR_PROVIDERS.has(i.provider) && i.status === 'active',
  );
  if (hasProvider) return null;

  const handleConnect = async (provider: 'google' | 'microsoft') => {
    if (provider === 'google') {
      // Mirror the email module: Google goes through the existing window event.
      window.dispatchEvent(new Event('triggerGoogleOAuth'));
      return;
    }
    try {
      await connectProvider({
        provider,
        authToken: token,
        capability: 'mail',
        conversationId,
      });
    } catch (err) {
      console.error('Failed to connect Microsoft account from chat:', err);
    }
  };

  return (
    <div className="module-empty-state chat-connect-prompt">
      <div className="module-empty-state__description">
        Connect your email so Xavior can help with your inbox, calendar, and contacts.
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
        <button
          type="button"
          className="module-empty-state__button module-empty-state__button--outline"
          onClick={() => handleConnect('google')}
        >
          <img src={googleLogo} width="14" height="14" alt="" />
          Connect Google
        </button>
        <button
          type="button"
          className="module-empty-state__button module-empty-state__button--outline"
          onClick={() => handleConnect('microsoft')}
        >
          <img src={microsoftLogo} width="14" height="14" alt="" />
          Connect Microsoft
        </button>
      </div>
    </div>
  );
}
